/**
 * CR043: READ Attempts Drawer Component
 */

import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { CheckCircle2, XCircle, RefreshCw, Image as ImageIcon } from "lucide-react";
import { READ_FAILURE_REASONS, type ReadFailureReason, type ReadAttemptLogEntry, type PocRunMerchantRead } from "@/lib/read-phase/types";
import { ReadStatusBadge } from "./ReadStatusBadge";
import { ReadQualityFlagChips } from "./ReadQualityFlagChips";

interface ReadAttemptsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchant: PocRunMerchantRead | null;
}

export function ReadAttemptsDrawer({ open, onOpenChange, merchant }: ReadAttemptsDrawerProps) {
  if (!merchant) return null;

  const attempts = merchant.read_attempts_log || [];
  const failureText = merchant.read_failure_reason 
    ? READ_FAILURE_REASONS[merchant.read_failure_reason as ReadFailureReason] || merchant.read_failure_reason
    : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>READ Attempts — {merchant.merchant_name || merchant.merchant_id}</SheetTitle>
          <SheetDescription>
            Source: {merchant.source_url || 'No URL'}
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-200px)] mt-4">
          <div className="space-y-4">
            {/* Attempts List */}
            {attempts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No attempt data recorded
              </div>
            ) : (
              attempts.map((attempt, idx) => (
                <Card key={idx}>
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="font-mono">
                          Attempt #{attempt.attempt_num}
                        </Badge>
                        {attempt.next_step === 'Accepted' && (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        )}
                        {attempt.next_step === 'Failed' && (
                          <XCircle className="h-4 w-4 text-red-500" />
                        )}
                        {attempt.next_step === 'Retrying…' && (
                          <RefreshCw className="h-4 w-4 text-blue-500" />
                        )}
                      </div>
                      <Badge 
                        variant={attempt.next_step === 'Accepted' ? 'default' : attempt.next_step === 'Failed' ? 'destructive' : 'secondary'}
                      >
                        {attempt.next_step}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                      <div>
                        <p className="text-muted-foreground text-xs">Quality Score</p>
                        <p className="font-medium">{attempt.quality_score?.toFixed(2) ?? '-'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Text Length</p>
                        <p className="font-medium">{attempt.text_length?.toLocaleString() ?? '-'} chars</p>
                      </div>
                    </div>

                    {attempt.actions && attempt.actions.length > 0 && (
                      <div className="mb-3">
                        <p className="text-muted-foreground text-xs mb-1">Actions</p>
                        <div className="flex flex-wrap gap-1">
                          {attempt.actions.map((action, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {action}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {attempt.flags && attempt.flags.length > 0 && (
                      <div className="mb-3">
                        <p className="text-muted-foreground text-xs mb-1">Flags</p>
                        <ReadQualityFlagChips flags={attempt.flags} maxVisible={5} />
                      </div>
                    )}

                    {attempt.excerpt && (
                      <div>
                        <p className="text-muted-foreground text-xs mb-1">Excerpt</p>
                        <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-h-24 whitespace-pre-wrap">
                          {attempt.excerpt.slice(0, 300)}
                          {attempt.excerpt.length > 300 && '...'}
                        </pre>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}

            <Separator />

            {/* Final Outcome Section */}
            <div className="space-y-3">
              <h4 className="font-medium">Final Outcome</h4>
              
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">Status:</span>
                <ReadStatusBadge status={merchant.read_status} />
              </div>

              {failureText && (
                <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded">
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">
                    Final Failure Reason
                  </p>
                  <p className="text-sm text-red-700 dark:text-red-300">{failureText}</p>
                </div>
              )}

              {merchant.read_failure_screenshot_url && (
                <div>
                  <p className="text-muted-foreground text-sm mb-2 flex items-center gap-1">
                    <ImageIcon className="h-4 w-4" />
                    Failure Screenshot
                  </p>
                  <img 
                    src={merchant.read_failure_screenshot_url} 
                    alt="Failure screenshot" 
                    className="rounded border max-w-full h-auto max-h-64 object-contain"
                  />
                </div>
              )}
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
